import { useCallback, useEffect, useState } from "react";

import { formatInt, formatUsd } from "@kontextwerk/web-common";

import CostSummaryCard from "../components/CostSummaryCard";
import CostTrendChart from "../components/CostTrendChart";
import FilterBar from "../components/FilterBar";
import PendingCostNotice from "../components/PendingCostNotice";
import StackedBreakdownChart from "../components/StackedBreakdownChart";
import { fetchCostSummary, fetchCostTimeseries } from "../lib/api";
import { defaultRange, type DateRange } from "../lib/dateRange";
import type {
  CostFilters,
  CostSummaryResponse,
  CostTimeseriesResponse,
} from "../lib/types";

export default function SelfRoute() {
  const [range, setRange] = useState<DateRange>(defaultRange(30));
  const [filters, setFilters] = useState<CostFilters>({});
  const [timeseries, setTimeseries] = useState<CostTimeseriesResponse | null>(null);
  const [opClassBreakdown, setOpClassBreakdown] = useState<CostSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ts, ops] = await Promise.all([
        fetchCostTimeseries(range.from, range.to, "day", filters),
        fetchCostSummary(range.from, range.to, "op_class", filters),
      ]);
      setTimeseries(ts);
      setOpClassBreakdown(ops);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const totalCost = timeseries?.buckets.reduce((a, b) => a + b.total_cost_usd, 0) ?? 0;
  const totalCalls = timeseries?.buckets.reduce((a, b) => a + b.total_calls, 0) ?? 0;

  return (
    <div className="p-6 space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-medium">Eigene Kosten</h1>
        <p className="text-sm text-kw-text-muted max-w-3xl">
          Server-seitig auf das eigene Subject gefiltert. Backend-Cost
          (L1 Embedding, L2 Extraction) wird per Design auf der Organization
          verbucht, nicht auf einem Subject — diese Sicht zeigt deshalb nur
          die L3-Consumer-paid Surface-Calls, die dir direkt zugeordnet sind.
        </p>
      </header>
      <FilterBar
        range={range}
        onRangeChange={setRange}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {error ? (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <PendingCostNotice pending={opClassBreakdown?.pending} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CostSummaryCard
          label="Eigene Summe (USD)"
          value={formatUsd(totalCost)}
          sub={`${formatInt(totalCalls)} eigene Calls`}
        />
        <CostSummaryCard
          label="Ø Cost/Call"
          value={formatUsd(totalCalls > 0 ? totalCost / totalCalls : 0)}
        />
      </div>

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
        <h2 className="text-sm font-medium text-kw-text-muted mb-2">
          Tages-Trend
        </h2>
        {loading && !timeseries ? (
          <p className="text-sm text-kw-text-faint">Lade…</p>
        ) : (
          <CostTrendChart buckets={timeseries?.buckets ?? []} />
        )}
      </section>

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
        <h2 className="text-sm font-medium text-kw-text-muted mb-2">
          Eigene Op-Class-Verteilung
        </h2>
        <StackedBreakdownChart buckets={opClassBreakdown?.buckets ?? []} />
      </section>
    </div>
  );
}
